import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";

const nav = [
    { to: "/services", label: "Services" },
    { to: "/industries", label: "Industries" },
    { to: "/support-plans", label: "Support Plans" },
    { to: "/projects", label: "Projects" },
    { to: "/about", label: "About" },
    { to: "/resources", label: "Resources" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-[background,border,backdrop-filter] duration-300 ${
                scrolled ? "backdrop-blur-xl bg-bg/75 border-b border-line" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-[68px] flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                    <span className="w-9 h-9 grid place-items-center bg-ink overflow-hidden">
                        <img src="/brand/logo-fct.png" alt="Final Cut Technologies" className="w-full h-full object-contain" />
                    </span>
                    <span className="hidden sm:flex flex-col leading-[1.05]">
                        <span className="font-display text-[15px] tracking-tight text-ink">Final Cut</span>
                        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted">Technologies</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-8">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[13px] tracking-tight link-underline ${isActive ? "text-ink" : "text-ink/75 hover:text-ink"}`
                            }
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        href="tel:+16046714247"
                        data-testid="header-support-cta"
                        className="hidden md:inline-flex items-center gap-2 text-[12px] font-mono tracking-[0.14em] uppercase text-ink/80 hover:text-gold transition-colors"
                    >
                        <Phone className="w-3.5 h-3.5" /> 604-671-4247
                    </a>
                    <Link
                        to="/contact"
                        data-testid="header-assessment-cta"
                        className="group inline-flex items-center gap-2 bg-ink text-bg text-[13px] font-medium px-4 py-2.5 border border-ink hover:bg-transparent hover:text-ink hover:border-gold transition-colors duration-300"
                    >
                        Request a Site Assessment
                        <ArrowUpRight className="w-4 h-4 group-hover:text-gold transition-colors" />
                    </Link>
                </div>

                <button
                    className="lg:hidden inline-flex items-center justify-center w-10 h-10 -mr-2"
                    onClick={() => setOpen((o) => !o)}
                    data-testid="mobile-menu-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                        className="lg:hidden bg-bg border-t border-line"
                        data-testid="mobile-nav"
                    >
                        <div className="px-6 py-6 flex flex-col gap-1">
                            {nav.map((n) => (
                                <Link key={n.to} to={n.to} className="py-3 font-display text-2xl tracking-tight text-ink border-b border-line">
                                    {n.label}
                                </Link>
                            ))}
                            <div className="mt-4 flex flex-col gap-3">
                                <Link to="/contact" className="bg-ink text-bg text-center py-3">Request a Site Assessment</Link>
                                <a href="tel:+16046714247" className="border border-ink text-ink text-center py-3 font-mono tracking-widest text-sm">Call 604-671-4247</a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
